import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import useStore from './useStore'
import { supabaseEnv } from '../lib/supabaseClient'
import {
  createClientPlan,
  deleteClientPlan,
  getClientPlan,
  listClientPlans,
  updateClientPlan,
} from '../lib/clientsApi'

// Strict cloud boundary: only the allowlisted plan settings leave the browser.
// Employee census rows and anything derived from them remain in useStore only.
const buildPlanSettings = () => useStore.getState().getPlanSettings()

const useClientBook = create(
  persist(
    (set, get) => ({
      // Persisted opt-in flag + which client is currently loaded.
      enabled: false,
      activeClientId: null,
      // Transient (re-fetched each session).
      clients: [],
      loading: false,
      saving: false,
      error: null,
      syncStatus: 'idle',
      lastSyncedAt: null,

      enableCloud: async () => {
        set({ enabled: true, error: null })
        return get().refresh()
      },
      disableCloud: () => set({
        enabled: false,
        activeClientId: null,
        clients: [],
        error: null,
        syncStatus: 'idle',
        lastSyncedAt: null,
      }),

      setSyncStatus: (syncStatus, options = {}) => set({
        syncStatus,
        ...(Object.prototype.hasOwnProperty.call(options, 'error') ? { error: options.error } : {}),
        ...(options.lastSyncedAt ? { lastSyncedAt: options.lastSyncedAt } : {}),
      }),

      // Keep the transient client list current after a background autosave
      // without re-fetching the whole book or showing a manual-saving state.
      markClientAutosaved: (row) => set((state) => ({
        clients: state.clients.map((client) => client.id === row.id ? row : client),
        syncStatus: 'saved',
        lastSyncedAt: row.updated_at || new Date().toISOString(),
        error: null,
      })),

      refresh: async () => {
        if (!get().enabled || !supabaseEnv.isReady) {
          set({ clients: [], loading: false })
          return []
        }
        set({ loading: true, error: null })
        try {
          const clients = await listClientPlans()
          const activeStillExists = clients.some((client) => client.id === get().activeClientId)
          set({
            clients,
            loading: false,
            activeClientId: activeStillExists ? get().activeClientId : null,
          })
          return clients
        } catch (error) {
          set({ error, loading: false })
          throw error
        }
      },

      saveAsNew: async (clientName) => {
        set({ saving: true })
        try {
          const row = await createClientPlan({
            clientName,
            planSettings: buildPlanSettings(),
          })
          set({ activeClientId: row.id, saving: false })
          await get().refresh()
          return row
        } catch (error) {
          set({ saving: false, error })
          throw error
        }
      },

      // Overwrite a specific saved client with the current plan settings and make
      // it the open client. Backs both the "Update" shortcut for the open client
      // and the per-row "save current settings here" action.
      saveToClient: async (id) => {
        if (!id) return null
        set({ saving: true })
        try {
          const row = await updateClientPlan(id, {
            planSettings: buildPlanSettings(),
          })
          set({ activeClientId: id, saving: false })
          await get().refresh()
          return row
        } catch (error) {
          set({ saving: false, error })
          throw error
        }
      },

      updateActive: async () => get().saveToClient(get().activeClientId),

      renameClient: async (id, clientName) => {
        const row = await updateClientPlan(id, { clientName })
        await get().refresh()
        return row
      },

      // Apply a saved client's plan settings without reading or replacing the
      // employee census currently held in the browser-local calculator store.
      // The client's saved comparison plans (B–D) load exactly as saved –
      // comparing is simply having more than one plan.
      selectClient: async (id) => {
        set({ loading: true, error: null })
        try {
          const row = await getClientPlan(id)
          useStore.getState().importPlanSettings(row?.plan_settings || {})
          useStore.temporal.getState().clear()
          set({ activeClientId: id, loading: false })
          return row
        } catch (error) {
          set({ error, loading: false })
          throw error
        }
      },

      removeClient: async (id) => {
        await deleteClientPlan(id)
        if (get().activeClientId === id) set({ activeClientId: null })
        await get().refresh()
      },

      // Stop tracking against a saved client (keeps the current working data).
      clearActive: () => set({ activeClientId: null }),
    }),
    {
      name: 'retira-client-book',
      version: 1,
      partialize: (state) => ({
        enabled: state.enabled,
        activeClientId: state.activeClientId,
      }),
    },
  ),
)

export default useClientBook
